import { Image, styled } from '@obvio/app'
import { RichText } from '@obvio/template'
import { Grid } from '@obvio/ui'

import { BlogDateCategory } from '../BlogDateCategory'
import {
  CenteredSection,
  MaxWidthContent,
} from '@/components/Renderer/CenteredSection'
import { HeroSection } from '@/components/Renderer/RendererVariants/HeroSection'

import type { DeepStringifyDate } from '@/typeUtils'
import type { Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type { ReactElement } from 'react'

const ContentWrap = styled(MaxWidthContent)`
  p {
    text-align: left;
  }

  h2 {
    font-size: 3.25rem;
    font-family: ${(theme) => theme.secondaryFont};
    font-weight: 300;
    line-height: 135%;
  }

  h3 {
    font-size: 2.25rem;
    font-weight: 300;
    font-family: ${(theme) => theme.secondaryFont};
    font-weight: 300;
    line-height: 135%;
  }
`

const BlogInfo = styled(BlogDateCategory)`
  margin: ${(theme) => theme.spacing.medium} 0;
`

const ImageWrap = styled.div`
  height: 32.3125rem;
  width: 100%;
  position: relative;
`

const ImagesGrid = styled(Grid)`
  width: 100%;
`

export const ARTICLE_SELECT = {
  category: {
    id: true,
    title: true,
  },
  id: true,
  slug: true,
  seoTitle: true,
  seoDescription: true,
  content: true,
  createdAt: true,
  image: true,
  title: true,
} as const

export type Article = DeepStringifyDate<
  DeepPick<Schema['Article'], typeof ARTICLE_SELECT>
>

export type PostProps = {
  article: Article
}

export function Post({ article }: PostProps): ReactElement {
  const img = article.image ?? '/static/img/blog_tło.jpeg'

  return (
    <>
      <HeroSection heading text={article.title} img={img} type="post" />
      <BlogInfo
        title={article.title}
        createdAt={article.createdAt}
        type="big"
      />
      {article.content.map(
        (
          section: string | { type: string; list?: any[]; columns?: number },
          index: number,
        ) => {
          if (typeof section === 'string') {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <CenteredSection key={index}>
                <ContentWrap>
                  <RichText value={section} />
                </ContentWrap>
              </CenteredSection>
            )
          }
          if (section.type === 'IMAGE') {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <CenteredSection key={index}>
                <ImagesGrid
                  templateColumns={`repeat(${
                    section.columns ?? 1
                  }, minmax(0,1fr))`}
                  gap="medium"
                >
                  {(section.list ?? []).map((image) => (
                    <ImageWrap key={image.name}>
                      <Image img={image} />
                    </ImageWrap>
                  ))}
                </ImagesGrid>
              </CenteredSection>
            )
          }
        },
      )}
    </>
  )
}
